/// <amd-module name="VSSUI/Components/List/ListDragSourceBehavior" />
import * as React from "react";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { beginDragOperation, DragDropEffect, DragImage, IDragDropOperation } from "VSSUI/Utilities/DragDrop";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { cellFromEvent } from "./List";
import { BoltListDragEvent, IDragDroppableUI, IListDragDropSecondaryData, IOverlayRenderProps } from "./List.Props";

interface IDragImageData {
    image: React.ReactNode;
    xOffset?: number;
    yOffset?: number;
}

interface IDragDroppableTableUI extends IDragDroppableUI {
    currentElement?: React.RefObject<HTMLElement>;
}

export interface IListDragSourceBehaviorOptions<D> {
    /**
     * A unique id for the drag source. Necessary for situations where
     * the drag source and drop target are separate lists.
     */
    id?: string;

    /**
     * Called with a dragend event when the drag operation has finished.
     * This will be called regardless of the result of the operation,
     * i.e. even if the dropEffect is none.
     */
    onDragEnd?: (event: BoltListDragEvent<HTMLElement, D>) => void;

    /**
     * Called with a dragstart event when a drag operation has been detected.
     * If a consumer only wants certain rows within a list to be draggable, this
     * property should be implemented, and the effectAllowed on the dataTransfer
     * should be set to none.
     */
    onDragStart?: (event: BoltListDragEvent<HTMLElement, D>) => void;

    /**
     * Called with dragover events while the mouse is moved over the list.
     * Additional logic can be implemented when dragging an item through the 
     * list. The default implementation allows to scroll when a dragging 
     * item approaches the top or bottom of the list.
     */
    onDragging?: (event: BoltListDragEvent<HTMLElement, D>) => void | boolean;

    /**
     * What to render inside of the <DragImage> that will follow the mouse as
     * the user decides where to drop the dragged item.
     *
     * Normally this will be a <ListDragImage /> - as in this example:
     *
     *  <ListDragImage text={event.detail.dataTransfer.data.myString} />
     *
     * Where the D of your behavior is a type that contains a myString property of type string.
     *
     */
    renderDragImage: (event: BoltListDragEvent<HTMLElement, D>) => React.ReactNode;

    /**
     * A string representing the type of data that is being acted upon in the
     * drag and drop operation.
     */
    type: string;
}

/**
 * A behavior that turns a list into the source of a drag and drop operation. Note - this
 * behavior should only be used if your list is intended to _only_ be a drag source. Please use
 * the ListDragDropBehavior instead if the list is also a drop target, as that is the only way
 * to get keyboard drag and drop support within your list.
 */
export class ListDragSourceBehavior<D> implements IBehavior<{}, IDragDroppableUI> {
    private dragImageData: IDragImageData | undefined;
    private dragDroppableUI: IDragDroppableUI;
    private eventDispatch: IEventDispatch;
    private itemProvider: IItemProvider<D>;
    private operation: IDragDropOperation | undefined;
    private options: IListDragSourceBehaviorOptions<D>;

    constructor(options: IListDragSourceBehaviorOptions<D>) {
        this.options = options;
    }

    public initialize = (props: any, dragDroppableUI: IDragDroppableUI, eventDispatch: IEventDispatch) => {
        this.dragDroppableUI = dragDroppableUI;
        this.eventDispatch = eventDispatch;

        this.eventDispatch.addEventListener("pointerdown", this.onPointerDown);
        this.eventDispatch.addEventListener("dragstart", this.onDragStart);
        this.eventDispatch.addEventListener("dragend", this.onDragEnd);
        this.eventDispatch.addEventListener("dragover", this.onDragging);

        this.itemProvider = props.itemProvider;
    };

    public componentDidUpdate(props: any) {
        this.itemProvider = props.itemProvider;
    }

    public componentWillUnmount() {
        this.eventDispatch?.removeEventListener("pointerdown", this.onPointerDown);
        this.eventDispatch?.removeEventListener("dragstart", this.onDragStart);
        this.eventDispatch?.removeEventListener("dragend", this.onDragEnd);
        this.eventDispatch?.removeEventListener("dragover", this.onDragging);
    }

    private beginDrag(event: React.PointerEvent<HTMLElement>) {
        const index = cellFromEvent(event).rowIndex;
        if (this.itemProvider && index >= 0) {
            const item = this.itemProvider.value[index];
            this.operation = beginDragOperation(event, {
                data: item,
                dropEffect: DragDropEffect.none,
                secondaryData: { index: index, sourceId: this.options.id } as IListDragDropSecondaryData,
                setDragImage: this.setDragImage,
                type: this.options.type
            });
        }
    }

    private onDragging = (event: BoltListDragEvent<HTMLElement, D>) => {
        if (this.options.onDragging) {
            this.options.onDragging(event);
            return;
        }

        this.onDraggingDefault();
    }

    private onDraggingDefault = () => {
        const scrollableElement = (this.dragDroppableUI as IDragDroppableTableUI)?.currentElement?.current;

        if (!scrollableElement) {
            return;
        };

        const {top, bottom} = scrollableElement.getBoundingClientRect();

        const speedRate = 20;
        const edgeRate = 0.05;

        const edgeSize = scrollableElement.offsetHeight * edgeRate;

        const viewportY = this.operation?.y?.value;

        if (!viewportY) {
            return;
        }

        const isInBottomEdge = bottom - edgeSize < viewportY;
        const isInTopEdge = top + edgeSize > viewportY;

        const canScrollUp = scrollableElement.scrollTop > 0
        const canScrollDown = scrollableElement.scrollTop + scrollableElement.offsetHeight < scrollableElement.scrollHeight;
    
        if (isInBottomEdge && canScrollDown) {
            scrollableElement.scrollTo({top: scrollableElement.scrollTop + speedRate});
        } else if (isInTopEdge && canScrollUp) {
            scrollableElement.scrollTo({top: scrollableElement.scrollTop - speedRate});
        }
    }

    private onDragEnd = (event: BoltListDragEvent<HTMLElement, D>) => {
        const index = cellFromEvent(event).rowIndex;

        if (index >= 0 && this.options.onDragEnd) {
            this.options.onDragEnd(event);
        }
        this.dragDroppableUI.removeOverlay("drag-source-item");
        this.dragImageData = undefined;
    };

    private onDragStart = (event: BoltListDragEvent<HTMLElement, D>) => {
        if (event.detail.dataTransfer) {
            const index = cellFromEvent(event).rowIndex;

            if (index >= 0) {
                if (this.options.onDragStart) {
                    this.options.onDragStart(event);
                }
                if (event.detail.dataTransfer.effectAllowed !== DragDropEffect.none) {
                    this.dragDroppableUI.addOverlay("drag-source-item", index, this.renderDragSourceItemOverlay);
                    if (this.dragImageData === undefined) {
                        this.dragImageData = {
                            image: this.options.renderDragImage(event)
                        };
                    }
                }
            }
        } else {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    private onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
        if (event.button === 0) {
            this.beginDrag(event);
        }
    };

    private renderDragSourceItemOverlay = (props: IOverlayRenderProps) => {
        return (
            <>
                <div className="bolt-list-drag-source-item flex-grow" />
                {this.operation && this.dragImageData && <DragImage operation={this.operation}>{this.dragImageData.image}</DragImage>}
            </>
        );
    };

    private setDragImage = (image: React.ReactNode, xOffset?: number, yOffset?: number) => {
        this.dragImageData = { image: image, xOffset: xOffset, yOffset: yOffset };
    };
}
